import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { SidePanel } from '@components/common/SidePanel';
import { ModalIndices } from '@/common/i-doc/i-doc.registry.ts';
import { collaborationPlan as c } from '@/styles/tokens';
import { toastService } from '@services/toast.service';
import { collaborationPlanService, CollaborationStep } from '@/services/collaboration-plan.service';
import { CollaborationPlanRow } from './CollaborationPlanRow';

type StepStatusFilter = 'pending' | 'done';

interface CollaborationPlanAllTasksPanelProps {
  open: boolean;
  onClose: () => void;
  clientId: number | string;
  /** Капований список картки (щонайбільше 4 активні) - тут не рендериться напряму, лише тригер
   *  релоаду: нова посилання після будь-якої дії (додати/редагувати/виконано/видалити - усі
   *  зрештою ведуть до `reloadCollaborationPlan` у `ClientDashboardTab`) запускає релоад власного
   *  (без ліміту) списку панелі. */
  items: CollaborationStep[];
  clientName: string;
  onComplete: (stepId: number) => void;
  onDelete: (stepId: number) => void;
  onEdit: (stepId: number) => void;
  onAdd: () => void;
}

/** Бокова панель "План співпраці" - повний список задач (на відміну від картки на дашборді, яка
 *  показує щонайбільше 4 активні). Відкривається з "Переглянути всі задачі" у `CollaborationPlanCard`.
 *  Побудована на спільному `SidePanel` - перша з багатьох подібних панелей проєкту.
 *
 *  Перемикач "Активні/Виконані" (не було в Figma, додано за запитом) - власний список через
 *  `GET .../collaboration-plan/all` (без ліміту й без прив'язки до капованого списку картки). */
export const CollaborationPlanAllTasksPanel: React.FC<CollaborationPlanAllTasksPanelProps> = ({
  open, onClose, clientId, items, clientName, onComplete, onDelete, onEdit, onAdd,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.collaborationPlan' });
  const [status, setStatus] = useState<StepStatusFilter>('pending');
  const [displayItems, setDisplayItems] = useState<CollaborationStep[]>([]);

  useEffect(() => {
    if (open) return;
    setStatus('pending');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    collaborationPlanService.getAllSteps(clientId, status).then(setDisplayItems).catch(() => {
      toastService.error('Не вдалося завантажити задачі');
    });
    // items - лише тригер релоаду (див. коментар типу вище), не дані для відображення.
  }, [open, status, clientId, items]);

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={t('title')}
      iDoc={ModalIndices.COLLABORATION_PLAN_ALL_TASKS}
      contentSx={{ px: 3, py: 1 }}
      headerActions={
        <ToggleButtonGroup
          size="small"
          exclusive
          value={status}
          onChange={(_, value: StepStatusFilter | null) => value && setStatus(value)}
          sx={{
            height: 32,
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontSize: 13, fontWeight: 500, px: 1.5, color: c.caption, border: `1px solid ${c.divider}`,
              '&.Mui-selected': { bgcolor: c.checkboxChecked, color: '#FFFFFF', '&:hover': { bgcolor: c.checkboxChecked } },
            },
          }}
        >
          <ToggleButton value="pending">{t('statusFilter.pending')}</ToggleButton>
          <ToggleButton value="done">{t('statusFilter.done')}</ToggleButton>
        </ToggleButtonGroup>
      }
      actions={
        <Button
          size="medium"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={onAdd}
          sx={{
            bgcolor: c.addButtonBg, color: c.addButtonText, borderRadius: '6px', px: 2,
            fontSize: 14, fontWeight: 500, textTransform: 'none', boxShadow: 'none',
            '&:hover': { bgcolor: c.addButtonBg, boxShadow: 'none' },
          }}
        >
          {t('addTask')}
        </Button>
      }
    >
      <Box>
        {displayItems.map((item, index) => (
          <CollaborationPlanRow
            key={item.id}
            item={item}
            isLast={index === displayItems.length - 1}
            clientName={clientName}
            onComplete={onComplete}
            onDelete={onDelete}
            onEdit={onEdit}
          />
        ))}
      </Box>
    </SidePanel>
  );
};
